import { Fragment, useEffect, useMemo, useState } from 'react'
import { LoaderBar } from 'components'
import {
    AuthService,
    RotationService,
    dateRangeForMonthYear,
    localDateToIso,
    rotationDetailOwnedByMonth,
} from 'utility'

import { MonthYearPicker } from './MonthYearPicker'
import { ViewerRoot } from './view-monthly-rotation-styles'

const AVATAR_COLORS = [
    { bg: '#2563eb', color: '#fff' },
    { bg: '#7c3aed', color: '#fff' },
    { bg: '#059669', color: '#fff' },
    { bg: '#db2777', color: '#fff' },
    { bg: '#d97706', color: '#fff' },
]

const getMonthYearBounds = (month, year) => {
    const { start, end } = dateRangeForMonthYear(month, year)
    return {
        startDate: localDateToIso(start),
        endDate: localDateToIso(end),
    }
}

const parseDate = value => {
    if (!value) return null
    const iso = String(value).slice(0, 10)
    const parts = iso.split('-').map(Number)
    if (parts.length === 3 && parts.every(n => !Number.isNaN(n))) {
        const [year, month, day] = parts
        return new Date(year, month - 1, day)
    }
    const d = new Date(value)
    return Number.isNaN(d.getTime()) ? null : d
}

const fmtShort = date => {
    if (!date) return '—'
    return date.toLocaleDateString('en-US', { month: 'short', day: 'numeric' })
}

const fmtFull = date => {
    if (!date) return '—'
    return date.toLocaleDateString('en-US', {
        month: 'short',
        day: '2-digit',
        year: 'numeric',
    })
}

const avatarColorForId = auditorId => {
    const idx = Math.abs(Number(auditorId) || 0) % AVATAR_COLORS.length
    return AVATAR_COLORS[idx]
}

const rowsOverlap = (a, b) => {
    const aStart = parseDate(a.FromDate)
    const aEnd = parseDate(a.ToDate)
    const bStart = parseDate(b.FromDate)
    const bEnd = parseDate(b.ToDate)
    if (!aStart || !aEnd || !bStart || !bEnd) return false
    return aStart <= bEnd && bStart <= aEnd
}

const isWeeklyRow = row => row.DurationDays != null && row.DurationDays === 7

const clipDateRange = (row, windowStartIso, windowEndIso) => {
    const from = parseDate(row.FromDate)
    const to = parseDate(row.ToDate)
    const winStart = parseDate(windowStartIso)
    const winEnd = parseDate(windowEndIso)
    if (!from || !to || !winStart || !winEnd) {
        return {
            displayFrom: from,
            displayTo: to,
            continued: false,
            continues: false,
            actualFrom: from,
            actualTo: to,
        }
    }
    const continued = from < winStart
    const continues = to > winEnd

    // Weekly Sun–Sat assignments: show the full saved range (may cross month boundaries).
    if (isWeeklyRow(row)) {
        return {
            displayFrom: from,
            displayTo: to,
            continued: false,
            continues: false,
            actualFrom: from,
            actualTo: to,
        }
    }

    return {
        displayFrom: continued ? winStart : from,
        displayTo: continues ? winEnd : to,
        continued,
        continues,
        actualFrom: from,
        actualTo: to,
    }
}

const clippedDurationDays = clip => {
    if (!clip.displayFrom || !clip.displayTo) return null
    const start = new Date(clip.displayFrom)
    const end = new Date(clip.displayTo)
    start.setHours(0, 0, 0, 0)
    end.setHours(0, 0, 0, 0)
    return Math.round((end - start) / 86400000) + 1
}

const formatDateRangeLabel = (clip, fallbackDays) => {
    const days = clippedDurationDays(clip) ?? fallbackDays
    const from = fmtShort(clip.displayFrom)
    const to = fmtShort(clip.displayTo)
    let range = `${from} – ${to}`
    if (days != null) range += ` (${days}d)`
    if (clip.continued) range += ' (continued)'
    if (clip.continues) range += ' (continues)'
    return range
}

const formatTemplateNames = name =>
    !name
        ? ''
        : String(name)
              .split(';')
              .map(s => s.trim())
              .filter(Boolean)
              .join(', ')

const formatDurationOnly = (clip, fallbackDays) => {
    const days = clippedDurationDays(clip) ?? fallbackDays
    if (days == null) return '—'
    let label = `${days} day${days === 1 ? '' : 's'}`
    if (clip.continued) label += ' (continued)'
    if (clip.continues) label += ' (continues)'
    return label
}

const getRowLabel = (row, clip, weekIndex) => {
    if (row.DurationDays != null && row.DurationDays < 7) return 'Partial'
    if (isWeeklyRow(row)) return `Week ${weekIndex}`
    return `${fmtShort(clip.displayFrom)} – ${fmtShort(clip.displayTo)}`
}

const groupItemsByAuditor = items => {
    const auditorMap = new Map()
    items.forEach(item => {
        if (!auditorMap.has(item.AuditorId)) auditorMap.set(item.AuditorId, [])
        auditorMap.get(item.AuditorId).push(item)
    })

    return [...auditorMap.entries()]
        .map(([auditorId, allRows]) => {
            const scheduleMap = new Map()
            allRows.forEach(row => {
                const key = row.RotationScheduleId
                if (!scheduleMap.has(key)) scheduleMap.set(key, [])
                scheduleMap.get(key).push(row)
            })

            const schedules = [...scheduleMap.entries()]
                .map(([scheduleId, rows]) => ({
                    scheduleId,
                    title: rows[0]?.Title || 'Rotation',
                    templateName: formatTemplateNames(rows[0]?.TemplateName),
                    shiftName: rows[0]?.ShiftName || '',
                    rows: [...rows].sort(
                        (a, b) =>
                            (parseDate(a.FromDate)?.getTime() || 0) -
                            (parseDate(b.FromDate)?.getTime() || 0),
                    ),
                }))
                .sort(
                    (a, b) =>
                        (parseDate(a.rows[0]?.FromDate)?.getTime() || 0) -
                        (parseDate(b.rows[0]?.FromDate)?.getTime() || 0),
                )

            const first = allRows[0]
            return {
                auditorId,
                auditorName: first?.AuditorName || '—',
                auditorInitials: first?.AuditorInitials || '?',
                status: allRows.some(r => r.Status === 'Inactive')
                    ? 'Inactive'
                    : first?.Status || 'Active',
                schedules,
            }
        })
        .sort((a, b) =>
            (a.auditorName || '').localeCompare(b.auditorName || ''),
        )
}

const distinctWorkplaces = items => {
    const seen = new Map()
    items.forEach(item => {
        if (item.WorkPlaceId != null && !seen.has(item.WorkPlaceId)) {
            seen.set(item.WorkPlaceId, item.WorkPlaceName || '—')
        }
    })
    return [...seen.entries()].map(([id, name]) => ({ id, name }))
}

const distinctAuditors = items => {
    const seen = new Map()
    items.forEach(item => {
        if (item.AuditorId != null && !seen.has(item.AuditorId)) {
            seen.set(item.AuditorId, item.AuditorName || '—')
        }
    })
    return [...seen.entries()].map(([id, name]) => ({ id, name }))
}

export const ViewMonthlyRotation = () => {
    const user = AuthService.getUser()
    const userId = user?.unique_id || user?.UserId || user?.Id

    const now = new Date()

    const [selectedMonth, setSelectedMonth] = useState(now.getMonth() + 1)
    const [selectedYear, setSelectedYear] = useState(now.getFullYear())
    const [viewerData, setViewerData] = useState(null)
    const [viewerLoading, setViewerLoading] = useState(false)

    const [viewerSearch, setViewerSearch] = useState('')
    const [viewerShift, setViewerShift] = useState('')
    const [viewerPlace, setViewerPlace] = useState('')
    const [viewerAuditor, setViewerAuditor] = useState('')

    const { startDate, endDate } = getMonthYearBounds(selectedMonth, selectedYear)

    const fetchViewerData = (from, to) => {
        if (!userId || !from || !to) return
        setViewerLoading(true)
        RotationService.getRotationViewer(userId, from, to)
            .then(res => setViewerData(res))
            .catch(err => console.error('Error fetching viewer data:', err))
            .finally(() => setViewerLoading(false))
    }

    useEffect(() => {
        const { startDate: from, endDate: to } = getMonthYearBounds(
            now.getMonth() + 1,
            now.getFullYear(),
        )
        fetchViewerData(from, to)
    }, [userId])

    const handleMonthYearChange = (month, year) => {
        setSelectedMonth(month)
        setSelectedYear(year)
    }

    const handleSearch = () => {
        fetchViewerData(startDate, endDate)
    }

    const handleClear = () => {
        setViewerSearch('')
        setViewerShift('')
        setViewerPlace('')
        setViewerAuditor('')
        const month = now.getMonth() + 1
        const year = now.getFullYear()
        setSelectedMonth(month)
        setSelectedYear(year)
        const { startDate: from, endDate: to } = getMonthYearBounds(month, year)
        fetchViewerData(from, to)
    }

    const items = viewerData?.Items || []
    const summary = viewerData?.Summary
    const monthLabel = viewerData?.MonthLabel || ''

    const monthOwnedItems = useMemo(
        () =>
            items.filter(item =>
                rotationDetailOwnedByMonth(item, selectedMonth, selectedYear),
            ),
        [items, selectedMonth, selectedYear],
    )

    const shiftOptions = useMemo(() => {
        const fromItems = new Map()
        monthOwnedItems.forEach(item => {
            if (item.ShiftId == null) return
            const key = String(item.ShiftId)
            if (!fromItems.has(key)) {
                fromItems.set(key, {
                    id: item.ShiftId,
                    name: item.ShiftName || `Shift ${item.ShiftId}`,
                    count: 0,
                })
            }
            fromItems.get(key).count += 1
        })
        if (fromItems.size > 0) {
            return [...fromItems.values()]
        }
        const breakdown = Array.isArray(summary?.ShiftBreakdown)
            ? summary.ShiftBreakdown
            : []
        return breakdown.map(s => ({
            id: s.ShiftId,
            name: s.ShiftName || `Shift ${s.ShiftId}`,
            count: s.Count,
        }))
    }, [monthOwnedItems, summary])

    const workplaceOptions = useMemo(
        () => distinctWorkplaces(monthOwnedItems),
        [monthOwnedItems],
    )
    const auditorOptions = useMemo(
        () => distinctAuditors(monthOwnedItems),
        [monthOwnedItems],
    )

    const filteredItems = useMemo(
        () =>
            monthOwnedItems.filter(item => {
                const q = viewerSearch.trim().toLowerCase()
                if (
                    q &&
                    !(item.AuditorName || '').toLowerCase().includes(q)
                ) {
                    return false
                }
                if (viewerShift && String(item.ShiftId) !== viewerShift) {
                    return false
                }
                if (viewerPlace && String(item.WorkPlaceId) !== viewerPlace) {
                    return false
                }
                if (viewerAuditor && String(item.AuditorId) !== viewerAuditor) {
                    return false
                }
                return true
            }),
        [monthOwnedItems, viewerSearch, viewerShift, viewerPlace, viewerAuditor],
    )

    const auditorGroups = useMemo(
        () => groupItemsByAuditor(filteredItems),
        [filteredItems],
    )

    const stats = useMemo(() => {
        const displayCount = monthOwnedItems.length
        const auditorCount = new Set(
            monthOwnedItems.map(item => item.AuditorId).filter(id => id != null),
        ).size
        const cards = [
            {
                icon: '📋',
                num: displayCount,
                lbl: 'Total Rotations',
                bg: '#dbeafe',
            },
        ]
        shiftOptions.forEach((shift, i) => {
            const icons = ['🌅', '☀️', '🌙', '🌿', '⏰']
            cards.push({
                icon: icons[i % icons.length],
                num: shift.count ?? 0,
                lbl: shift.name,
                bg: i % 2 === 0 ? '#fef3c7' : '#dbeafe',
            })
        })
        cards.push({
            icon: '👥',
            num: auditorCount,
            lbl: 'Auditors',
            bg: '#d1fae5',
        })
        return cards
    }, [monthOwnedItems, shiftOptions])

    const hasActiveFilters =
        viewerSearch || viewerShift || viewerPlace || viewerAuditor

    const emptyMessage = () => {
        if (monthOwnedItems.length === 0 && monthLabel) {
            return `No rotations scheduled for ${monthLabel}.`
        }
        if (hasActiveFilters) return 'No rotations match your filters.'
        return 'No rotations found for this period.'
    }

    return (
        <ViewerRoot>
            {viewerLoading && <LoaderBar content="Loading viewer…" />}

            <div style={{ marginBottom: '20px' }}>
                <div className="viewer-page-title">
                    {monthLabel || 'Rotation Schedule Viewer'}
                </div>
                <div className="viewer-page-subtitle">
                    View all team rotation assignments for the selected period
                </div>
            </div>

            {stats.length > 0 && (
                <div className="viewer-stats">
                    {stats.map((st, i) => (
                        <div key={i} className="viewer-stat-card">
                            <div
                                style={{
                                    width: '40px',
                                    height: '40px',
                                    borderRadius: '10px',
                                    background: st.bg,
                                    display: 'flex',
                                    alignItems: 'center',
                                    justifyContent: 'center',
                                    fontSize: '20px',
                                    flexShrink: 0,
                                }}
                            >
                                {st.icon}
                            </div>
                            <div>
                                <div
                                    style={{
                                        fontSize: '22px',
                                        fontWeight: 700,
                                        color: '#1a2744',
                                        lineHeight: 1,
                                    }}
                                >
                                    {st.num}
                                </div>
                                <div
                                    style={{
                                        fontSize: '11px',
                                        color: '#6b7a9e',
                                        marginTop: '2px',
                                    }}
                                >
                                    {st.lbl}
                                </div>
                            </div>
                        </div>
                    ))}
                </div>
            )}

            <div className="viewer-filters">
                <div className="viewer-search-wrap viewer-filter-field">
                    <span className="viewer-search-icon">🔍</span>
                    <input
                        type="text"
                        className="viewer-search-input"
                        placeholder="Search auditor…"
                        value={viewerSearch}
                        onChange={e => setViewerSearch(e.target.value)}
                    />
                </div>

                <div className="viewer-filter-field">
                    <span className="viewer-filter-label">Shift</span>
                    <select
                        className="viewer-select"
                        value={viewerShift}
                        onChange={e => setViewerShift(e.target.value)}
                    >
                        <option value="">All Shifts</option>
                        {shiftOptions.map(s => (
                            <option key={s.id} value={String(s.id)}>
                                {s.name}
                            </option>
                        ))}
                    </select>
                </div>

                <div className="viewer-filter-field">
                    <span className="viewer-filter-label">Workplace</span>
                    <select
                        className="viewer-select"
                        value={viewerPlace}
                        onChange={e => setViewerPlace(e.target.value)}
                    >
                        <option value="">All Workplaces</option>
                        {workplaceOptions.map(w => (
                            <option key={w.id} value={String(w.id)}>
                                {w.name}
                            </option>
                        ))}
                    </select>
                </div>

                <div className="viewer-filter-field">
                    <span className="viewer-filter-label">Auditor</span>
                    <select
                        className="viewer-select"
                        value={viewerAuditor}
                        onChange={e => setViewerAuditor(e.target.value)}
                    >
                        <option value="">All Auditors</option>
                        {auditorOptions.map(a => (
                            <option key={a.id} value={String(a.id)}>
                                {a.name}
                            </option>
                        ))}
                    </select>
                </div>
            </div>

            <div className="viewer-month-bar">
                <span className="viewer-month-label">📅 Month</span>
                <div className="viewer-month-picker">
                    <MonthYearPicker
                        month={selectedMonth}
                        year={selectedYear}
                        onChange={handleMonthYearChange}
                        disabled={viewerLoading}
                        compact
                    />
                </div>
                <button
                    type="button"
                    className="viewer-btn-primary"
                    onClick={handleSearch}
                    disabled={viewerLoading}
                >
                    Search
                </button>
                <button
                    type="button"
                    className="viewer-btn-ghost"
                    onClick={handleClear}
                >
                    ✕ Clear
                </button>
            </div>

            <div className="viewer-legend">
                <div className="viewer-legend-item">
                    <div
                        style={{
                            width: '10px',
                            height: '10px',
                            borderRadius: '3px',
                            background: '#fef3c7',
                        }}
                    />
                    Partial week (&lt;7 days)
                </div>
                <div className="viewer-legend-item">
                    <div
                        style={{
                            width: '10px',
                            height: '10px',
                            borderRadius: '3px',
                            background: '#fef3c7',
                            border: '1px solid #f59e0b',
                        }}
                    />
                    Location changed
                </div>
                <div className="viewer-legend-item">
                    <div
                        style={{
                            width: '8px',
                            height: '8px',
                            borderRadius: '50%',
                            background: '#ef4444',
                        }}
                    />
                    Overlapping dates
                </div>
            </div>

            {!viewerLoading &&
                (auditorGroups.length === 0 ? (
                    <div className="viewer-empty">
                        <div className="viewer-empty-icon">🗂️</div>
                        <div className="viewer-empty-text">{emptyMessage()}</div>
                    </div>
                ) : (
                    <div className="viewer-auditor-list">
                        {auditorGroups.map(group => {
                            const av = avatarColorForId(group.auditorId)
                            const isInactive = group.status === 'Inactive'
                            const scheduleCount = group.schedules.length
                            const singleScheduleMeta =
                                scheduleCount === 1
                                    ? [
                                          group.schedules[0].title,
                                          group.schedules[0].templateName,
                                      ]
                                          .filter(Boolean)
                                          .join(' · ')
                                    : ''

                            return (
                                <div
                                    key={group.auditorId}
                                    className={`viewer-auditor-card${
                                        isInactive ? ' is-inactive' : ''
                                    }`}
                                >
                                    <div className="viewer-auditor-header">
                                        <div
                                            className="viewer-avatar"
                                            style={{
                                                background: av.bg,
                                                color: av.color,
                                            }}
                                        >
                                            {group.auditorInitials}
                                        </div>
                                        <div style={{ flex: 1, minWidth: '160px' }}>
                                            <div className="viewer-auditor-name">
                                                {group.auditorName}
                                                {isInactive && (
                                                    <span className="viewer-inactive-badge">
                                                        Inactive
                                                    </span>
                                                )}
                                            </div>
                                            {scheduleCount > 1 ? (
                                                <div className="viewer-auditor-meta">
                                                    {scheduleCount} rotation schedules
                                                </div>
                                            ) : (
                                                singleScheduleMeta && (
                                                    <div className="viewer-auditor-meta">
                                                        {singleScheduleMeta}
                                                    </div>
                                                )
                                            )}
                                        </div>
                                    </div>

                                    {group.schedules.map(schedule => {
                                        const metaParts = [
                                            schedule.title,
                                            schedule.templateName,
                                        ].filter(Boolean)
                                        const showScheduleHeader =
                                            group.schedules.length > 1 ||
                                            schedule.rows.length > 1
                                        let weekCounter = 0

                                        return (
                                            <div key={schedule.scheduleId}>
                                                {showScheduleHeader && (
                                                    <div className="viewer-schedule-header">
                                                        <div className="viewer-schedule-title">
                                                            {metaParts.join(' · ')}
                                                        </div>
                                                        {schedule.shiftName && (
                                                            <div className="viewer-shift-pill">
                                                                {schedule.shiftName}
                                                            </div>
                                                        )}
                                                    </div>
                                                )}

                                                <table className="viewer-week-table">
                                                    <tbody>
                                                        {schedule.rows.map(
                                                            (row, rowIdx) => {
                                                                const isPartial =
                                                                    row.DurationDays !=
                                                                        null &&
                                                                    row.DurationDays < 7
                                                                const prevRow =
                                                                    rowIdx > 0
                                                                        ? schedule.rows[
                                                                              rowIdx - 1
                                                                          ]
                                                                        : null
                                                                const locationChanged =
                                                                    prevRow &&
                                                                    prevRow.WorkPlaceId !==
                                                                        row.WorkPlaceId
                                                                const clip = clipDateRange(
                                                                    row,
                                                                    startDate,
                                                                    endDate,
                                                                )
                                                                const hasOverlap =
                                                                    schedule.rows
                                                                        .slice(0, rowIdx)
                                                                        .some(prev =>
                                                                            rowsOverlap(
                                                                                prev,
                                                                                row,
                                                                            ),
                                                                        )
                                                                const isLongSpan =
                                                                    row.DurationDays !=
                                                                        null &&
                                                                    row.DurationDays > 7

                                                                if (isWeeklyRow(row)) {
                                                                    weekCounter += 1
                                                                }

                                                                const rowLabel = getRowLabel(
                                                                    row,
                                                                    clip,
                                                                    weekCounter,
                                                                )

                                                                const dateTooltip = [
                                                                    clip.continued &&
                                                                        `Started ${fmtFull(clip.actualFrom)}`,
                                                                    clip.continues &&
                                                                        `Ends ${fmtFull(clip.actualTo)}`,
                                                                ]
                                                                    .filter(Boolean)
                                                                    .join(' · ')

                                                                const dateCellContent =
                                                                    isLongSpan
                                                                        ? formatDurationOnly(
                                                                              clip,
                                                                              row.DurationDays,
                                                                          )
                                                                        : formatDateRangeLabel(
                                                                              clip,
                                                                              row.DurationDays,
                                                                          )

                                                                return (
                                                                    <Fragment
                                                                        key={
                                                                            row.RotationDetailId
                                                                        }
                                                                    >
                                                                        <tr className="viewer-week-row">
                                                                            <td
                                                                                className={`viewer-week-label-cell${
                                                                                    isPartial
                                                                                        ? ' is-partial'
                                                                                        : ''
                                                                                }`}
                                                                                style={{
                                                                                    color: isPartial
                                                                                        ? undefined
                                                                                        : '#6b7a9e',
                                                                                    fontWeight:
                                                                                        isPartial
                                                                                            ? undefined
                                                                                            : 400,
                                                                                }}
                                                                            >
                                                                                {rowLabel}
                                                                            </td>
                                                                            <td
                                                                                className="viewer-week-dates-cell"
                                                                                title={
                                                                                    dateTooltip ||
                                                                                    undefined
                                                                                }
                                                                            >
                                                                                {dateCellContent}
                                                                                {row.SendEmail && (
                                                                                    <span
                                                                                        title="Email notification enabled"
                                                                                        style={{
                                                                                            marginLeft:
                                                                                                '6px',
                                                                                        }}
                                                                                    >
                                                                                        ✉️
                                                                                    </span>
                                                                                )}
                                                                                {hasOverlap && (
                                                                                    <span
                                                                                        title="Overlapping assignment — contact support"
                                                                                        style={{
                                                                                            marginLeft:
                                                                                                '6px',
                                                                                            display:
                                                                                                'inline-block',
                                                                                            width: '8px',
                                                                                            height: '8px',
                                                                                            borderRadius:
                                                                                                '50%',
                                                                                            background:
                                                                                                '#ef4444',
                                                                                        }}
                                                                                    />
                                                                                )}
                                                                            </td>
                                                                            <td className="viewer-week-place-cell">
                                                                                <span
                                                                                    className={`viewer-place-chip ${
                                                                                        locationChanged
                                                                                            ? 'is-changed'
                                                                                            : 'is-default'
                                                                                    }`}
                                                                                >
                                                                                    {row.WorkPlaceName ||
                                                                                        '—'}
                                                                                </span>
                                                                            </td>
                                                                        </tr>
                                                                        {row.Comments && (
                                                                            <tr className="viewer-week-comments-row">
                                                                                <td colSpan={3}>
                                                                                    <div className="viewer-week-comments">
                                                                                        {
                                                                                            row.Comments
                                                                                        }
                                                                                    </div>
                                                                                </td>
                                                                            </tr>
                                                                        )}
                                                                    </Fragment>
                                                                )
                                                            },
                                                        )}
                                                    </tbody>
                                                </table>
                                            </div>
                                        )
                                    })}
                                </div>
                            )
                        })}
                    </div>
                ))}
        </ViewerRoot>
    )
}
